import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { isDefined } from "@omni/utils/helper.utils";
import { isEmpty } from "lodash-es";
import { useEffect, useState } from "react";

import { useBFFProductServiceApi } from "@/api/hooks/productService.hook";
import { ProductSliderSkeletonComponent } from "@/components/Product/ProductSlider/components/skeleton/productSliderSkeleton.component";
import { ProductSliderSourceContextProvider } from "@/components/Product/ProductSlider/context/productSliderSource.context";
import { filterVisible } from "@/components/Product/ProductSlider/productSlider.helpers";
import { extractAccessoires } from "@/components/Product/ProductSlider/sources/accessories/accessories.helpers";

type AccessoriesProductSliderProps = {
    sku: string;
    serverSideProducts?: Array<Product<ProductFieldSet.ProductTile>>;
};

export const AccessoriesProductSlider = ({
    sku,
    children,
    serverSideProducts
}: RequireChildrenFor<AccessoriesProductSliderProps>) => {
    const [products, setProducts] = useState(serverSideProducts);

    const [{ loading, error }, loadProducts] = useBFFProductServiceApi<
        any,
        any,
        Array<Product<ProductFieldSet.Full>>
    >(
        {
            url: `/products`,
            method: "GET"
        },
        {
            manual: true,
            useCache: false,
            autoCancel: false
        }
    );

    useEffect(() => {
        if (!sku) return;
        if (isDefined(products)) return;
        if (loading || error) return;

        loadProducts({
            params: {
                sku: sku,
                fields: ProductFieldSet.Full
            }
        })
            .then(({ data }) => data?.at(0))
            .then(extractAccessoires)
            .then(filterVisible)
            .then(setProducts);
    }, [products, sku, loading, error, loadProducts]);

    if (!sku || error) {
        return null;
    } else if (isDefined(products) && isEmpty(products)) {
        return null;
    } else if (isDefined(products) && !isEmpty(products)) {
        return (
            <ProductSliderSourceContextProvider products={products}>
                {children}
            </ProductSliderSourceContextProvider>
        );
    } else {
        return <ProductSliderSkeletonComponent />;
    }
};
